import { defineStore } from "pinia";

import { MerchantApiError } from "../authentication/errors.js";
import {
  defaultStoreSettingsDraft,
  draftFromProfile,
  validateStoreSettingsDraft,
  type StoreSettingsDraft,
  type StoreSettingsProfile,
} from "./models.js";
import type { StoreSettingsService } from "./service.js";

export const useStoreSettingsStore = defineStore("merchant-store-settings", {
  state: (): {
    draft: StoreSettingsDraft;
    errorMessage?: string;
    loading: boolean;
    mutationMessage?: string;
    profile?: StoreSettingsProfile;
    saving: boolean;
    service?: StoreSettingsService;
    validationErrors: string[];
  } => ({
    draft: defaultStoreSettingsDraft(),
    loading: false,
    saving: false,
    validationErrors: [],
  }),
  actions: {
    configure(service: StoreSettingsService): void {
      this.service = service;
    },
    async load(): Promise<void> {
      this.loading = true;
      delete this.errorMessage;
      try {
        this.applyProfile(await this.requireService().loadStore());
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Store settings could not be loaded.");
      } finally {
        this.loading = false;
      }
    },
    async save(successMessage = "Store settings saved."): Promise<void> {
      if (this.profile === undefined) return;
      this.validationErrors = validateStoreSettingsDraft(this.draft);
      if (this.validationErrors.length > 0) return;

      this.saving = true;
      delete this.errorMessage;
      delete this.mutationMessage;
      try {
        this.applyProfile(await this.requireService().saveStore(cloneDraft(this.draft)));
        this.mutationMessage = successMessage;
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Store settings could not be saved.");
      } finally {
        this.saving = false;
      }
    },
    applyProfile(profile: StoreSettingsProfile): void {
      this.profile = profile;
      this.draft = draftFromProfile(profile);
    },
    requireService(): StoreSettingsService {
      if (this.service === undefined) {
        throw new Error("Store settings store was not configured before use.");
      }
      return this.service;
    },
  },
});

function cloneDraft(draft: StoreSettingsDraft): StoreSettingsDraft {
  return {
    businessHours: { ...draft.businessHours, weekdays: [...draft.businessHours.weekdays] },
    reviewSlaMinutes: draft.reviewSlaMinutes,
  };
}

function messageForError(error: unknown, fallback: string): string {
  if (error instanceof MerchantApiError && error.kind === "access") {
    return "Only Owners and App Admins can change store settings.";
  }
  if (error instanceof MerchantApiError && error.kind === "validation") return error.message;
  return error instanceof Error ? error.message : fallback;
}
